import {
  ADVANCED_ZMANIM_ENGLISH_LABELS,
  ADVANCED_ZMANIM_YIDDISH_MAPPING,
  ZMANIM_YIDDISH_MAPPING,
} from '@/utils/const';
import { MOTZEI_SHABBOS_LONDON_KEY } from '@/utils/motzei_shabbos_london';

type ZmanimSection = 'basic' | 'advanced' | 'special';

export interface KosherZmanimDropdownOption {
  key: string;
  value: string;
  label: string;
  section: ZmanimSection;
  sectionLabel: string;
  category: string;
  categoryLabel: string;
}

const BASIC_MAPPING = ZMANIM_YIDDISH_MAPPING as Record<string, string>;
const ADVANCED_MAPPING = ADVANCED_ZMANIM_YIDDISH_MAPPING as Record<string, string>;
// Keys shown under "Special Zmanim" instead of Basic/Advanced. Motzei Shabbos
// London lives in ADVANCED_ZMANIM_YIDDISH_MAPPING (the label source every option
// is built from) but is surfaced here, so it is excluded from Advanced below.
const SPECIAL_ZMANIM_KEYS = ['shabbatHadlukasHaneiros', 'shabbatMotzei', MOTZEI_SHABBOS_LONDON_KEY];

const SECTION_LABELS: Record<ZmanimSection, string> = {
  basic: 'Basic',
  advanced: 'Advanced',
  special: 'Special Zmanim',
};

const CATEGORY_ORDER = ['dawn', 'morning', 'midday', 'afternoon', 'evening', 'other'];
const CATEGORY_LABELS: Record<string, string> = {
  dawn: 'Dawn',
  morning: 'Morning',
  midday: 'Mid Day',
  afternoon: 'Afternoon',
  evening: 'Evening',
  other: 'Other',
};

const ALL_ZMANIM_LABELS: Record<string, string> = {
  ...BASIC_MAPPING,
  ...ADVANCED_MAPPING,
};

function getZmanimCategory(key: string): string {
  if (!key) return 'other';
  if (key.startsWith('alos') || key.startsWith('misheyakir')) return 'dawn';
  if (key === 'sunrise' || key === 'seaLevelSunrise' || key.startsWith('sofZmanShma') || key.startsWith('sofZmanTfila')) return 'morning';
  if (key === 'chatzos' || key === 'chatzosHalfDay') return 'midday';
  if (key.startsWith('minchaGedola') || key.startsWith('minchaKetana') || key.startsWith('plagHamincha')) return 'afternoon';
  if (
    key === 'sunset'
    || key === 'seaLevelSunset'
    || key === 'shabbatHadlukasHaneiros'
    || key === 'shabbatMotzei'
    || key === 'chatzotNight'
    || key === 'midnightTonight'
    || key === 'solarMidnight'
    || key === MOTZEI_SHABBOS_LONDON_KEY
    || key.startsWith('tzais')
  ) {
    return 'evening';
  }
  return 'other';
}

function formatZmanimOptionLabel(key: string): string {
  return key
    .replace(new RegExp('([a-z0-9])([A-Z])', 'g'), '$1 $2')
    .replace(new RegExp('([A-Z])([A-Z][a-z])', 'g'), '$1 $2')
    .replace(new RegExp('(\\d)([A-Za-z])', 'g'), '$1 $2')
    .replace(new RegExp('([A-Za-z])(\\d)', 'g'), '$1 $2')
    .trim();
}

function makeOption(key: string, section: ZmanimSection): KosherZmanimDropdownOption {
  const category = getZmanimCategory(key);
  return {
    key,
    value: key,
    label: ALL_ZMANIM_LABELS[key] || formatZmanimOptionLabel(key),
    section,
    sectionLabel: SECTION_LABELS[section],
    category,
    categoryLabel: CATEGORY_LABELS[category] || CATEGORY_LABELS.other,
  };
}

function sortLikeZmanimBasicTab(options: KosherZmanimDropdownOption[]): KosherZmanimDropdownOption[] {
  return [...options].sort((a, b) => CATEGORY_ORDER.indexOf(a.category) - CATEGORY_ORDER.indexOf(b.category));
}

export function getMinyanimKosherZmanimOptions(): KosherZmanimDropdownOption[] {
  const basic = Object.keys(BASIC_MAPPING)
    .filter((key) => !SPECIAL_ZMANIM_KEYS.includes(key))
    .map((key) => makeOption(key, 'basic'));

  const advanced = Object.keys(ADVANCED_MAPPING)
    .filter((key) => !BASIC_MAPPING[key] && !SPECIAL_ZMANIM_KEYS.includes(key))
    .map((key) => makeOption(key, 'advanced'));

  const special = SPECIAL_ZMANIM_KEYS
    .filter((key) => ALL_ZMANIM_LABELS[key])
    .map((key) => makeOption(key, 'special'));

  return [
    ...sortLikeZmanimBasicTab(basic),
    ...sortLikeZmanimBasicTab(advanced),
    ...special,
  ];
}

export function getKosherZmanimOptionLabel(key: string): string {
  if (!key) return '';
  return ALL_ZMANIM_LABELS[key] || formatZmanimOptionLabel(key);
}

export interface SimpleZmanOption {
  value: string;
  label: string;
}

const ENGLISH_LABELS = ADVANCED_ZMANIM_ENGLISH_LABELS as Record<string, string>;

// Evening (nightfall) zmanim that map 1:1 to a kosher-zmanim ComplexZmanimCalendar
// getter (get<Key>()), so the backend can compute the exact time for the chosen
// Day Shift boundary. Minute-based keys without a real getter (tzais18/20/24/30/42)
// and tzaisGeonim5Point61Degrees are intentionally excluded — kosher-zmanim 0.9.x
// has no method for them, so they could never be resolved server-side.
const COMPUTABLE_EVENING_KEYS: string[] = [
  'sunset',
  'tzais50',
  'tzais60',
  'tzais72',
  'tzais90',
  'tzais96',
  'tzais120',
  'tzaisGeonim3Point65Degrees',
  'tzaisGeonim3Point676Degrees',
  'tzaisGeonim4Point37Degrees',
  'tzaisGeonim4Point61Degrees',
  'tzaisGeonim4Point8Degrees',
  'tzaisGeonim5Point95Degrees',
  'tzaisGeonim6Point45Degrees',
  'tzaisGeonim7Point083Degrees',
  'tzaisGeonim7Point67Degrees',
  'tzaisGeonim8Point5Degrees',
  'tzaisAteretTorah',
];

// The one Day Shift that is not a zman: the day rolls over at 12 AM on the
// normal clock while the organization stays on the Hebrew calendar. It is
// stored in `eveningZman` like a zman key, and the backend checks for it
// before looking for a kosher-zmanim getter (keep the two in step).
export const DAY_SHIFT_MIDNIGHT_KEY = '12am';
const DAY_SHIFT_MIDNIGHT_LABEL = '12 AM (Midnight)';

// Options for the organization's "Day Shift" — the time at which the system
// rolls over to the next day. English labels (Settings UI is English): 12 AM
// first, then Sunset, then Tzeis by minutes, then by degrees.
export function getEveningKosherZmanimOptions(): SimpleZmanOption[] {
  return [
    { value: DAY_SHIFT_MIDNIGHT_KEY, label: DAY_SHIFT_MIDNIGHT_LABEL },
    ...COMPUTABLE_EVENING_KEYS.map((key) => ({
      value: key,
      label: ENGLISH_LABELS[key] || formatZmanimOptionLabel(key),
    })),
  ];
}

// Human label for a stored evening-zman key (falls back to a formatted key).
export function getEveningZmanLabel(key: string): string {
  if (!key) return '';
  if (key === DAY_SHIFT_MIDNIGHT_KEY) return DAY_SHIFT_MIDNIGHT_LABEL;
  return ENGLISH_LABELS[key] || formatZmanimOptionLabel(key);
}
